import type { Origin } from "@rally/ui-kit";
import { type Attachment, useAttaching } from "../attachment/attaching.ts";
import { canonicalTypedPayload } from "../message/message-payload.ts";
import { Composer } from "./composer.tsx";

export function ReplyComposer({
  conversation,
  to,
  said,
  who,
  files,
  links,
  onAdd,
  onSay,
  onFiles,
  onClose,
}: {
  conversation: string;
  to: string;
  said: string;
  who: Parameters<typeof Composer>[0]["who"];
  files: Parameters<typeof Composer>[0]["files"];
  links: readonly string[];
  onAdd: NonNullable<Parameters<typeof Composer>[0]["onAdd"]>;
  onSay: (
    text: string,
    from: Origin | null,
    files: readonly Attachment[],
    submission: string,
  ) => Promise<boolean>;
  onFiles: () => void;
  onClose: (target: string) => void;
}) {
  const attaching = useAttaching(conversation);
  return (
    <Composer
      conversation={conversation}
      draftKey={`${conversation}:reply:${to}`}
      testID={`reply-composer-${to}`}
      replyContext={{ said, onClose: () => onClose(to) }}
      autoFocus
      who={who}
      files={files}
      links={links}
      onAdd={onAdd}
      picked={attaching.picked}
      onPick={attaching.take}
      onDrop={attaching.drop}
      onSay={async (text, from, identify) => {
        const carried = await attaching.ready();
        if (!text && carried.length === 0) return false;
        const files = carried.map(({ key, name, mediaType }) => ({ key, name, mediaType }));
        const submission = identify(
          canonicalTypedPayload({
            conversation,
            text,
            replyTo: { kind: "message", id: to },
            files,
          }),
        );
        const landed = await onSay(text, from, carried, submission);
        if (!landed) {
          attaching.retry(carried);
          return false;
        }
        attaching.taken(carried);
        if (carried.length > 0) onFiles();
        onClose(to);
        return true;
      }}
    />
  );
}
